import { createFileRoute, Link } from "@tanstack/react-router";
import { trackConversion } from "@/lib/gtag";
import { Phone, Briefcase, Clock, ShieldCheck, Plane, Receipt, Users } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { StickyMobileBar } from "@/components/StickyMobileBar";
import { IdleCallNudge } from "@/components/IdleCallNudge";

const URL = "https://sepremiumcabs.com/corporate-taxi";
const TITLE = "Corporate Taxi Melbourne | Business Travel — SE Premium Cabs";
const DESC =
  "Corporate taxi service in Melbourne for business travel and airport runs. Account invoicing, fixed upfront fares, 24/7 pre-booking. Call 0493 100 265.";

const FAQS = [
  {
    q: "Do you provide corporate taxi accounts in Melbourne?",
    a: "Yes. We set up business accounts with monthly invoicing so staff and clients can travel without handling payment in the car. Call 0493 100 265 or email sepremiumcabs@gmail.com to set one up.",
  },
  {
    q: "Can we book staff and client trips in advance?",
    a: "Yes. Trips can be pre-booked hours, days or weeks ahead, including recurring travel. We recommend booking airport pickups at least 2 hours ahead.",
  },
  {
    q: "What vehicles are available for business travel?",
    a: "Premium Sedan (up to 4 passengers, 3 bags), Premium SUV (up to 6 passengers, 5 bags) and Maxi (up to 11 passengers, 9 bags) for larger groups or roadshows.",
  },
  {
    q: "How is a corporate trip priced?",
    a: "You get a fixed upfront fare before the trip starts — no surge pricing. Account customers receive an itemised monthly invoice.",
  },
];

export const Route = createFileRoute("/corporate-taxi")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://sepremiumcabs.com/og-image.jpg" },
      { name: "twitter:image", content: "https://sepremiumcabs.com/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Corporate taxi and business travel",
          name: "Corporate Taxi Melbourne",
          description: DESC,
          areaServed: "Melbourne, Victoria, Australia",
          provider: {
            "@type": "LocalBusiness",
            name: "SE Premium Cabs",
            telephone: "+61493100265",
            email: "sepremiumcabs@gmail.com",
            url: "https://sepremiumcabs.com/",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://sepremiumcabs.com/" },
            { "@type": "ListItem", position: 2, name: "Corporate Taxi Melbourne", item: URL },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: CorporatePage,
});

function CorporatePage() {
  const points = [
    { icon: Receipt, title: "Account Invoicing", body: "Monthly itemised invoices instead of drivers collecting payment from staff or clients in the car." },
    { icon: Clock, title: "Pre-Booked & 24/7", body: "Book breakfast meetings, red-eye departures and late-night returns around the clock, including public holidays." },
    { icon: Plane, title: "Airport Runs", body: "Fixed-fare transfers to and from Melbourne Airport (Tullamarine) with flight tracking and meet & greet." },
    { icon: ShieldCheck, title: "Accredited Drivers", body: "CPV-accredited, presentable drivers who know Melbourne's road network and peak-hour alternatives." },
    { icon: Users, title: "Group & Roadshow Travel", body: "Maxi vehicles for up to 11 passengers when a team, delegation or client group travels together." },
    { icon: Briefcase, title: "Fixed Upfront Fares", body: "A quoted fare before travel — nothing to reconcile against surge pricing on an expense claim." },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <div className="mx-auto max-w-4xl px-6 py-12 pb-28 lg:py-20 lg:pb-20">
        <nav className="mb-8 text-xs font-semibold tracking-widest text-muted-foreground">
          <Link to="/" className="hover:text-primary">HOME</Link>
          <span className="mx-2">/</span>
          <span className="text-primary">CORPORATE TAXI</span>
        </nav>

        <h1 className="font-serif text-4xl font-bold tracking-tight lg:text-5xl">
          Corporate Taxi Melbourne
          <br />
          <span className="text-primary">for Business Travel</span>
        </h1>
        <p className="mt-6 text-base leading-relaxed text-muted-foreground lg:text-lg">
          SE Premium Cabs handles business travel across Melbourne — executive airport runs,
          client pickups, inter-office trips and after-hours staff transport. Trips can be
          pre-booked or called in on demand, priced with a fixed upfront fare, and billed to a
          monthly account so nobody has to claim taxi receipts.
        </p>

        <div className="mt-8 flex flex-wrap gap-4">
          <a
            href="tel:0493100265"
            onClick={() => trackConversion("phoneCall")}
            className="flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-bold tracking-wider text-primary-foreground transition-transform hover:scale-[1.02]"
          >
            <Phone className="h-4 w-4" /> CALL 0493 100 265
          </a>
          <Link
            to="/"
            hash="book"
            className="flex items-center gap-2 rounded-md border border-primary px-6 py-3 text-sm font-bold tracking-wider text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
          >
            BOOK ONLINE
          </Link>
        </div>

        <section className="mt-14">
          <h2 className="font-serif text-2xl font-bold tracking-tight">What a corporate account includes</h2>
          <div className="mt-2 h-px w-16 bg-primary" />
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {points.map((p) => (
              <div key={p.title} className="rounded-lg border border-border/60 bg-card p-5">
                <div className="flex items-center gap-3">
                  <p.icon className="h-5 w-5 text-primary" />
                  <h3 className="text-sm font-bold tracking-wider">{p.title}</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14">
          <h2 className="font-serif text-2xl font-bold tracking-tight">How to set up an account</h2>
          <div className="mt-2 h-px w-16 bg-primary" />
          <ol className="mt-6 space-y-4 text-sm leading-relaxed text-muted-foreground">
            <li>
              <strong className="text-foreground">1. Call or email us.</strong> Ring{" "}
              <a href="tel:0493100265" onClick={() => trackConversion("phoneCall")} className="text-primary">0493 100 265</a>{" "}
              or email <a href="mailto:sepremiumcabs@gmail.com" className="text-primary">sepremiumcabs@gmail.com</a> with your
              business name, billing contact and typical travel pattern.
            </li>
            <li>
              <strong className="text-foreground">2. Agree the booking method.</strong> Most accounts book by phone or through
              the online form; recurring trips can be standing bookings.
            </li>
            <li>
              <strong className="text-foreground">3. Travel, then reconcile monthly.</strong> Each trip is quoted upfront and
              appears on one itemised invoice at the end of the month.
            </li>
          </ol>
        </section>

        <section className="mt-14">
          <h2 className="font-serif text-2xl font-bold tracking-tight">Corporate travel questions</h2>
          <div className="mt-2 h-px w-16 bg-primary" />
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {FAQS.map((f) => (
              <details key={f.q} className="group rounded-lg border border-border/60 bg-card p-5 open:border-primary/60">
                <summary className="flex cursor-pointer items-center justify-between gap-4 text-sm font-semibold">
                  <span>{f.q}</span>
                  <span className="text-primary transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mt-14 rounded-xl border border-primary/40 bg-card p-8 text-center">
          <h2 className="font-serif text-2xl font-bold tracking-tight">Set up business travel with us</h2>
          <p className="mt-3 text-sm text-muted-foreground">
            Also see our{" "}
            <Link to="/melbourne-airport-taxi" className="text-primary">Melbourne Airport transfers</Link>,{" "}
            <Link to="/chauffeur-services" className="text-primary">chauffeur service</Link> and{" "}
            <Link to="/long-distance-taxi" className="text-primary">long-distance trips</Link>.
          </p>
          <a
            href="tel:0493100265"
            onClick={() => trackConversion("phoneCall")}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-bold tracking-wider text-primary-foreground"
          >
            <Phone className="h-4 w-4" /> CALL 0493 100 265
          </a>
        </section>
      </div>
      <SiteFooter />
      <StickyMobileBar />
      <IdleCallNudge />
    </div>
  );
}
